import { Day } from "../types/core";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const SECOND = 1000;
export const MINUTE = 60 * SECOND;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

export function dateToDayWithTimezone(date: Date, timezoneOverride?: string): Day {
    if (timezoneOverride) {
        const formatter = new Intl.DateTimeFormat(navigator.language, {
            timeZone: timezoneOverride,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        const parts = formatter.formatToParts(date);
        let year = '';
        let month = '';
        let day = '';
        for (const part of parts) {
            if (part.type === 'year') {
                year = part.value;
            } else if (part.type === 'month') {
                month = part.value;
            } else if (part.type === 'day') {
                day = part.value;
            }
        }
        return +(year + month + day) as Day;
    } else {
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const monthString = month < 10 ? '0' + month : '' + month;
        const dayString = day < 10 ? '0' + day : '' + day;
        return +(year + monthString + dayString) as Day;
    }
}

export function dayMinusDays(day: Day, deltaDays: number): Day {
    return dateToDayBrowserTimezone(dateMinusDays(dayToDateBrowserTimezone(day), deltaDays));
}

export function dateToDayBrowserTimezone(date: Date): Day {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const monthString = month < 10 ? '0' + month : '' + month;
    const dayString = day < 10 ? '0' + day : '' + day;
    return +(year + monthString + dayString) as Day;
}

export function dayToDateBrowserTimezone(numberedDay: Day): Date {
    if (!numberedDay) {
        return new Date();
    }
    const dayString = `${numberedDay}`;
    const year = Number(dayString.substring(0, 4));
    const month = Number(dayString.substring(4, 6)) - 1;
    const day = Number(dayString.substring(6, 8));
    return new Date(year, month, day); // browser timezone, which is needed for date pickers
}

/**
 * Seconds from the route's own midnight, allowed past 24h.
 *
 * The planned times in a RouteInfo are cumulative from metaStartTime and
 * routinely exceed a day, while a plain seconds-of-day reading wraps — so
 * comparing the two directly made every actual recorded after midnight look a
 * whole day out. Both sides have to be measured from the same origin.
 */
export function secondsFromDayStart(millis: number, day: Day) {
    return (millis - dayToDateBrowserTimezone(day).getTime()) / 1000;
}

export function dayToDateWithTimezone(dayIn: Day, timezoneOverride: string): Date {
    if (!dayIn) {
        return new Date();
    }

    const numberedDay = `${dayIn}`;
    // Extract YYYY, MM, DD from a string like '20250310'
    const year = numberedDay.substring(0, 4);
    const month = numberedDay.substring(4, 6);
    const day = numberedDay.substring(6, 8);

    // Build a dayjs object at midnight of the specified time zone
    const dayjsDate = dayjs.tz(
        `${year}-${month}-${day} 00:00:00`,
        timezoneOverride
    );

    // Convert back to a JavaScript Date
    return dayjsDate.toDate();
}

export function dayToMillisBrowserTimezone(numberedDay: Day): number {
    return dayToDateBrowserTimezone(numberedDay).getTime();
}

export function isoDateToDay(dateString: string): Day {
    const numberString = dateString.replace(/-/g, "");
    return parseInt(numberString, 10) as Day;
}

/**
 * Rewrites the 24-hour clock times inside a sentence to the reader's preference.
 *
 * Co-Dispatch's reasons and evidence ("Not available yet — shift starts at 13:00")
 * are composed by the engine, server-side, and one analysis is shown to every
 * operator in the company — so the format cannot be chosen where the sentence is
 * built. The engine always emits zero-padded HH:MM, which makes this substitution
 * unambiguous; localizing at render is the only place that can honour a per-user
 * setting. Whole hours keep their minutes ("8:00 AM"), so a list of times lines up, and a range says its AM or PM
 * once ("9:30–11:59 PM").
 */
export function localizeClockTimes(text: string, isAmPm: boolean): string {
    if (!isAmPm || !text) return text;
    return text
        .replace(/\b([01]\d|2[0-3]):([0-5]\d)\b/g, (match, hours: string, minutes: string) => {
            const time = createTimeStringFromInt(true, Number(hours) * 100 + Number(minutes));
            return time ? time.replace(/^(\d+) /, "$1:00 ") : match;
        })
        .replace(/\b(\d{1,2}:\d\d) ([AP]M) ?– ?(\d{1,2}:\d\d) \2\b/g, "$1–$3 $2");
}

export function createTimeStringFromInt(isAmPm: boolean, i: number) {
    let s = "" + i;
    while (s.length < 4) {
        s = "0" + s;
    }
    if (isAmPm) {
        let time = i;
        let ampm: string;
        if (time === 2400) {
            time = 1200;
            ampm = " AM";
        } else if (time < 100) {
            time += 1200;
            ampm = " AM";
        } else if (time < 1200) {
            ampm = " AM";
        } else if (time < 1300) {
            ampm = " PM";
        } else {
            time -= 1200;
            ampm = " PM";
        }
        const h = Math.floor(time / 100);
        const m = Math.floor(time % 100);
        if (m > 0) {
            let sb = "" + h + ":";
            if (m < 10) {
                sb += "0";
            }
            sb += (m + ampm);
            return sb;
        } else {
            return h + ampm;
        }
    } else {
        return s.substring(0, 2) + ":" + s.substring(2);
    }
}

// Calendar days, not 24 h steps: the days the clocks change have 23 or 25 hours.
export function datePlusDays(old: Date, days: number): Date {
    const date = new Date(old);
    date.setDate(date.getDate() + days);
    return date;
}

export function dateMinusDays(old: Date, days: number): Date {
    return datePlusDays(old, -days);
}

export function floorDate(original: Date) {
    const date = new Date(original);
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    return date;
}

export function timeToDate(time: number): Date {
    const date = new Date();
    date.setHours(Math.floor(time / 100));
    date.setMinutes(time % 100);
    date.setSeconds(0);
    return date;
}

export function dateToTime(date: Date): number {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return Number(`${hours}${minutes < 10 ? '0' + minutes : minutes}`);
}

export function dayOfToday(): Day {
    return dateToDayBrowserTimezone(new Date());
}

export function dayOfMinus(days: number): Day {
    return dateToDayBrowserTimezone(dateMinusDays(new Date(), days));
}

export function getDatesBetween(startDate: Date, endDate: Date): number[] {
    const dates: Date[] = [];

    const currentDate = new Date(startDate);
    const stopDate = new Date(endDate);

    while (currentDate <= stopDate) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates.map((date) => dateToDayBrowserTimezone(date));
}

export function millisToMinutes(millis: number) {
    const date = new Date(millis);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return (hours * 60) + minutes; 
}
